import { useEffect, useState } from 'react';
import { Modal, ModalActions, ModalNote } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { useChangePasswordMutation } from '../../entities/session/api/sessionApi';
import { EyeIcon, EyeOffIcon } from '../../assets/icons/icons';
import styles from './ChangePasswordModal.module.scss';
import React from 'react';

/** Требование бэкенда и старой формы: не короче десяти символов. */
const MIN_LENGTH = 10;

export interface ChangePasswordModalProps {
    open: boolean;
    onClose: () => void;
    /** Вызывается после успешной смены: пароль изменился, сессию нужно закрыть. */
    onSuccess: () => void;
}

export function ChangePasswordModal({ open, onClose, onSuccess }: ChangePasswordModalProps) {
    const [password, setPassword] = useState('');
    const [repeat, setRepeat] = useState('');
    /* У каждого поля свой глазик: показать одно, не раскрывая второе. */
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [repeatVisible, setRepeatVisible] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [changePassword, { isLoading }] = useChangePasswordMutation();

    /* Черновик не переживает закрытие окна: пароль в состоянии держать незачем. */
    useEffect(() => {
        if (open) return;
        setPassword('');
        setRepeat('');
        setPasswordVisible(false);
        setRepeatVisible(false);
        setError(null);
    }, [open]);

    if (!open) return null;

    const tooShort = password.length > 0 && password.length < MIN_LENGTH;
    const mismatch = repeat.length > 0 && repeat !== password;
    const canSubmit = password.length >= MIN_LENGTH && repeat === password && !isLoading;

    const handleSubmit = async () => {
        setError(null);
        try {
            await changePassword(password).unwrap();
            onSuccess();
        } catch {
            setError('Произошла ошибка при смене пароля');
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            width={418}
            title="Изменить пароль"
            closeOnOverlayClick={false}
            footer={
                <>
                    <ModalNote>После смены пароля потребуется войти заново.</ModalNote>
                    <ModalActions>
                        <Button onClick={onClose} disabled={isLoading}>
                            Отменить
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
                            Изменить
                        </Button>
                    </ModalActions>
                </>
            }
        >
            <label className={styles.fieldWrap}>
                <span className={styles.fieldLabel}>Новый пароль</span>

                <span className={styles.field}>
                    <input
                        type={passwordVisible ? 'text' : 'password'}
                        value={password}
                        autoComplete="new-password"
                        onChange={(e) => setPassword(e.target.value)}
                    />

                    <button
                        type="button"
                        className={styles.fieldAction}
                        onClick={() => setPasswordVisible((visible) => !visible)}
                        aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                        {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                </span>

                <small className={tooShort ? styles.error : styles.hint}>
                    Придумайте пароль, в котором содержится от {MIN_LENGTH} символов
                </small>
            </label>

            <label className={styles.fieldWrap}>
                <span className={styles.fieldLabel}>Повторите пароль</span>

                <span className={styles.field}>
                    <input
                        type={repeatVisible ? 'text' : 'password'}
                        value={repeat}
                        autoComplete="new-password"
                        onChange={(e) => setRepeat(e.target.value)}
                    />

                    <button
                        type="button"
                        className={styles.fieldAction}
                        onClick={() => setRepeatVisible((visible) => !visible)}
                        aria-label={repeatVisible ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                        {repeatVisible ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                </span>

                {mismatch && <small className={styles.error}>Пароли не совпадают</small>}
            </label>

            {error && <p className={styles.formError}>{error}</p>}
        </Modal>
    );
}